import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import TopPick from './components/TopPick';
import Filters from './components/Filters';
import ResultsTable from './components/ResultsTable';
import ThinkingOrb from './components/ThinkingOrb';
import { useAuth } from './context/AuthContext';
import { fetchInsights, getReportStatus, markReportUsed } from './services/api';
import { exportToExcel } from './utils/excelExport';

const TABS = [
  { key: 'products', label: 'Products' },
  { key: 'communities', label: 'Communities' },
  { key: 'topics', label: 'Topics' },
];

function App() {
  const { isAuthenticated, logout, loading: authLoading, user, token, isPaid } = useAuth();

  const [activeTab, setActiveTab] = useState('products');
  const [results, setResults] = useState({ products: [], communities: [], topics: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  // Filters state per tab
  const [filters, setFilters] = useState({ products: {}, communities: {}, topics: {} });
  const [selectedCategory, setSelectedCategory] = useState('');

  // Free tier tracking
  const [reportStatus, setReportStatus] = useState(null);

  // Fetch report status on auth
  useEffect(() => {
    const fetchStatus = async () => {
      if (isAuthenticated && token) {
        try {
          const status = await getReportStatus(token);
          setReportStatus(status);
        } catch (e) {
          // ignore
        }
      }
    };
    fetchStatus();
  }, [isAuthenticated, token]);

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const currentResults = results[activeTab] || [];
  const topPick = currentResults.length > 0 ? currentResults[0] : null;

  const canGenerate = isPaid || (reportStatus && reportStatus.can_generate);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  const handleGenerate = async () => {
    if (activeTab === 'products' && !selectedCategory) {
      setError('Please select a category for Products.');
      return;
    }

    if (!canGenerate) {
      setError('Free tier limit reached (1 report/day). Click "Upgrade to Paid" for unlimited reports.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const insights = await fetchInsights(token, activeTab, selectedCategory, filters[activeTab] || {});
      setResults(prev => ({ ...prev, [activeTab]: insights }));

      // Mark report as used
      if (token) {
        try {
          const status = await markReportUsed(token);
          setReportStatus(prev => ({
            ...prev,
            reports_today: status.reports_today,
            can_generate: status.can_generate,
          }));
        } catch (e) {
          // non-critical
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch insights');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!isPaid) {
      setError('Export is available for Paid users only. Click "Upgrade to Paid" to unlock.');
      return;
    }
    try {
      await exportToExcel(currentResults, activeTab);
    } catch (err) {
      setError('Failed to export. Please try again.');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, [activeTab]: newFilters }));
  };

  // Admin view
  if (showAdmin && user?.role === 'admin') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-left">
            <span className="header-icon">&#9655;</span>
            <h1>Admin Panel</h1>
          </div>
          <div className="header-actions">
            <button onClick={() => setShowAdmin(false)} className="header-btn">
              Terminal
            </button>
            <button onClick={logout} className="header-btn header-btn-logout">
              Logout
            </button>
          </div>
        </header>
        <main className="App-main">
          <AdminPanel token={token} onLogout={logout} />
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <span className="header-icon">&#9655;</span>
          <h1>Trend Hunters</h1>
          {!isPaid && (
            <span className="tier-badge tier-free">FREE</span>
          )}
          {isPaid && user?.role !== 'admin' && (
            <span className="tier-badge tier-paid">PAID</span>
          )}
          {user?.role === 'admin' && (
            <span className="tier-badge tier-admin">ADMIN</span>
          )}
        </div>
        <div className="header-actions">
          {user?.role === 'admin' && (
            <button onClick={() => setShowAdmin(true)} className="header-btn">
              Admin
            </button>
          )}
          <button onClick={logout} className="header-btn header-btn-logout">
            Logout
          </button>
        </div>
      </header>

      <main className="App-main">
        {/* Tabs */}
        <div className="tabs-bar">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`tab-btn ${activeTab === tab.key ? 'tab-active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Filters
          activeTab={activeTab}
          filters={filters[activeTab] || {}}
          onFilterChange={handleFilterChange}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* Generate button */}
        <div className="generate-section">
          <button
            onClick={handleGenerate}
            disabled={loading || !canGenerate}
            className="generate-btn"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Scanning opportunities...
              </>
            ) : (
              `Scan ${TABS.find(t => t.key === activeTab)?.label || ''}`
            )}
          </button>
          {!isPaid && reportStatus && (
            <div className="report-limit-info">
              {reportStatus.can_generate
                ? `${1 - (reportStatus.reports_today || 0)} report remaining today`
                : 'Daily limit reached — upgrade for unlimited'}
            </div>
          )}
          {!isPaid && (
            <div className="upgrade-banner">
              &#9889; To upgrade to the Paid plan, contact <strong>dan@trafficgenerationsecrets.net</strong>
            </div>
          )}
        </div>

        {/* Error */}
        {error && <div className="error-banner">{error}</div>}

        {/* Top Pick */}
        {topPick && <TopPick item={topPick} onGenerateSimilar={null} />}

        {/* Results Table or Thinking Orb */}
        {currentResults.length > 0 ? (
          <ResultsTable
            results={currentResults}
            isPaid={isPaid}
            onExport={handleExport}
          />
        ) : !loading ? (
          <ThinkingOrb />
        ) : null}
      </main>
    </div>
  );
}

export default App;
